import { useEffect, useRef, useState, useContext } from "react";
import AuthContext from "../context/AuthContext";

function wsUrl() {
  const protocol = window.location.protocol === "https:" ? "wss" : "ws";
  const base = window.location.host;

  return `${protocol}://${base}/ws/app/`;
}

export function useAppSocket() {
  const { token } = useContext(AuthContext);
  const [status, setStatus] = useState("disconnected");
  const wsRef = useRef(null);
  const retryRef = useRef(500);

  useEffect(() => {
    if (!token) return;
    let closed = false;

    function connect() {
      const ws = new WebSocket(wsUrl(), ["Token", token]);
      wsRef.current = ws;
      setStatus("connecting");

      ws.addEventListener("open", () => {
        setStatus("connected");
        retryRef.current = 500;
      });

      ws.addEventListener("close", () => {
        setStatus("disconnected");
        if (!closed) {
          const delay = Math.min(retryRef.current, 8000);
          setTimeout(connect, delay);
          retryRef.current *= 2;
        }
      });

      ws.addEventListener("error", () => {
        try {
          ws.close();
        } catch {}
      });
    }

    connect();
    return () => {
      closed = true;
      wsRef.current?.close();
    };
  }, [token]);

  return { wsRef, status };
}

export function useProvideAppSocket() {
  const { token } = useContext(AuthContext);
  const [status, setStatus] = useState("disconnected");
  const [ws, setWs] = useState(null);

  const retryRef = useRef(500);
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  useEffect(() => {
    if (!token) {
      // ensure consumers know there's no socket
      setWs(null);
      setStatus("disconnected");
      return;
    }

    let closed = false;

    const connect = () => {
      const s = new WebSocket(wsUrl(), ["Token", token]);
      socketRef.current = s;
      setWs(s);
      setStatus("connecting");

      const onOpen = () => {
        setStatus("connected");
        retryRef.current = 500;
      };

      const onClose = () => {
        setStatus("disconnected");
        if (!closed) {
          const delay = Math.min(retryRef.current, 8000);
          reconnectTimerRef.current = setTimeout(connect, delay);
          retryRef.current *= 2;
        }
      };

      const onError = () => {
        // ensure we transition through close path
        try {
          s.close();
        } catch (e) {}
      };

      s.addEventListener("open", onOpen);
      s.addEventListener("close", onClose);
      s.addEventListener("error", onError);

      // per-socket cleanup
      s._cleanup = () => {
        s.removeEventListener("open", onOpen);
        s.removeEventListener("close", onClose);
        s.removeEventListener("error", onError);
        try {
          s.close();
        } catch (e) {}
      };
    };

    connect();

    return () => {
      // cleanup runs on dep change or unmount (even if no events fired)
      closed = true;

      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      if (socketRef.current && socketRef.current._cleanup) {
        socketRef.current._cleanup();
      }
      socketRef.current = null;
      setWs(null);
    };
  }, [token]);

  return { ws, status };
}
